import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, ScanSearch, CheckCircle, AlertTriangle, Wrench, X } from 'lucide-react';
import { Button } from '../ui/button';
import LessonVideoSource from '../course/LessonVideoSource';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  broken: 'bg-red-500/15 text-red-400 border-red-500/30',
  unrecognized: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  missing: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  unknown: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  ok: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
};
const STATUS_LABELS = {
  broken: 'Broken',
  unrecognized: 'Bad link',
  missing: 'No video',
  unknown: 'Check manually',
  ok: 'Playable',
};

// Admin card: scans every video lesson, lists the unplayable ones, and lets
// the admin fix each one inline (URL / upload / library + Test playback).
export default function VideoScanCard({ getAuthHeader }) {
  const [scanning, setScanning] = useState(false);
  const [data, setData] = useState(null);
  const [fixingId, setFixingId] = useState(null);
  const [fixUrl, setFixUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [showOk, setShowOk] = useState(false);

  const runScan = async () => {
    setScanning(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/lms/video-scan`, { headers: getAuthHeader() });
      setData(r.data);
      if (r.data.summary.needs_attention === 0) {
        toast.success('All lesson videos are playable');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Scan failed');
    } finally {
      setScanning(false);
    }
  };

  const startFix = (row) => {
    setFixingId(row.lesson_id);
    setFixUrl(row.video_url || '');
  };

  const saveFix = async (lessonId) => {
    if (!fixUrl.trim()) {
      toast.error('Choose or paste a video first');
      return;
    }
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/admin/lessons/${lessonId}/video`,
        { video_url: fixUrl.trim() },
        { headers: getAuthHeader() },
      );
      toast.success('Lesson video updated — re-scanning…');
      setFixingId(null);
      await runScan();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update lesson');
    } finally {
      setSaving(false);
    }
  };

  const issues = data ? data.results.filter((r) => r.status !== 'ok') : [];
  const okRows = data ? data.results.filter((r) => r.status === 'ok') : [];

  const renderRow = (row) => (
    <div
      key={row.lesson_id}
      className="rounded-md border border-border p-3 space-y-2"
      data-testid={`video-scan-row-${row.lesson_id}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm text-foreground font-medium truncate">
            {row.course_title || 'Unknown course'} › {row.module_title || 'Module'} › {row.lesson_title}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">{row.reason}</p>
          {row.video_url && (
            <p className="text-[11px] text-muted-foreground/70 truncate mt-0.5">{row.video_url}</p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full border ${STATUS_STYLES[row.status]}`}>
            {STATUS_LABELS[row.status]}
          </span>
          {row.status !== 'ok' && fixingId !== row.lesson_id && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => startFix(row)}
              data-testid={`video-scan-fix-btn-${row.lesson_id}`}
            >
              <Wrench className="w-3.5 h-3.5 mr-1" /> Fix video
            </Button>
          )}
        </div>
      </div>

      {fixingId === row.lesson_id && (
        <div className="rounded-md bg-white/5 p-3 space-y-3" data-testid="video-scan-fix-panel">
          <p className="text-xs text-muted-foreground">
            Paste a new link, upload an MP4, or pick from the Video Library — then use{' '}
            <span className="text-foreground">Test playback</span> to confirm it plays before saving.
          </p>
          <LessonVideoSource value={fixUrl} onChange={setFixUrl} getAuthHeader={getAuthHeader} />
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={() => saveFix(row.lesson_id)}
              disabled={saving}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="video-scan-save-fix"
            >
              {saving ? 'Saving…' : 'Save fix'}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setFixingId(null)}
              data-testid="video-scan-cancel-fix"
            >
              <X className="w-3.5 h-3.5 mr-1" /> Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="wm-card p-6 rounded-lg border border-border space-y-4" data-testid="video-scan-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Lesson Video Health</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Checks every course lesson's video — YouTube/Vimeo availability, uploaded files, and bad
            links — and lets you fix broken ones right here.
          </p>
        </div>
        <Button onClick={runScan} disabled={scanning} data-testid="video-scan-run">
          {scanning ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Scanning…</>
          ) : (
            <><ScanSearch className="w-4 h-4 mr-2" /> Scan lesson videos</>
          )}
        </Button>
      </div>

      {data && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-3 text-sm" data-testid="video-scan-summary">
            <span className="inline-flex items-center gap-1.5 text-emerald-400">
              <CheckCircle className="w-4 h-4" /> {data.summary.ok} playable
            </span>
            <span className={`inline-flex items-center gap-1.5 ${data.summary.needs_attention ? 'text-red-400' : 'text-muted-foreground'}`}>
              <AlertTriangle className="w-4 h-4" /> {data.summary.needs_attention} need attention
            </span>
            {data.summary.unknown > 0 && (
              <span className="text-blue-400">{data.summary.unknown} couldn't be verified</span>
            )}
            <span className="text-muted-foreground">of {data.summary.total} video lessons</span>
          </div>

          {issues.length > 0 && <div className="space-y-2">{issues.map(renderRow)}</div>}

          {okRows.length > 0 && (
            <button
              type="button"
              onClick={() => setShowOk((s) => !s)}
              className="text-xs text-muted-foreground hover:text-foreground underline"
              data-testid="video-scan-show-ok"
            >
              {showOk ? 'Hide' : 'Show'} {okRows.length} playable lessons
            </button>
          )}
          {showOk && <div className="space-y-2">{okRows.map(renderRow)}</div>}
        </div>
      )}
    </div>
  );
}
